import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, Phone } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Spinner } from "react-bootstrap";
import { resendOtp, verifyRegistrationOtp } from "../../../services/auth.api";

/**
 * Registration OTP screen. Verifying the code is what creates the session —
 * the API answers with `{ user, token }`, which is stored on the spot.
 *
 * Password reset no longer passes through here: the API takes the OTP and the
 * new password in one call, so that lives in ResetPasswordForm.
 *
 * @param {string} phone - the number the code was sent to
 * @param {function} goback - back to the previous step
 * @param {function} onVerify - called once the session is established
 */
const VerificationForm = ({ phone, goback, onVerify }) => {
  const [code, setCode] = useState(Array(6).fill(""));
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [error, setError] = useState("");
  const [resendCountdown, setResendCountdown] = useState(0);

  const inputRefs = useRef([]);
  const storedPhone = phone || localStorage.getItem("verificationPhone") || "";

  useEffect(() => {
    setResendCountdown(60);
    inputRefs.current[0]?.focus();
  }, []);

  useEffect(() => {
    if (phone) localStorage.setItem("verificationPhone", phone);
  }, [phone]);

  useEffect(() => {
    if (!resendCountdown) return;
    const t = setTimeout(() => setResendCountdown((v) => v - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCountdown]);

  const handleChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;

    const next = [...code];
    next[index] = value.slice(-1);
    setCode(next);
    setError("");

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    if (next.every(Boolean)) {
      handleVerify(next.join(""));
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (!pasted) return;

    const next = Array(6).fill("");
    pasted.split("").forEach((d, i) => (next[i] = d));
    setCode(next);

    if (pasted.length === 6) {
      handleVerify(pasted);
    } else {
      inputRefs.current[pasted.length]?.focus();
    }
  };

  const handleVerify = async (value) => {
    if (!storedPhone) {
      setError("Verification details not found. Please go back and try again.");
      return;
    }

    setIsLoading(true);
    try {
      await verifyRegistrationOtp({ phone: storedPhone, otp: value });
      localStorage.removeItem("verificationPhone");
      onVerify?.();
    } catch (err) {
      setError(err?.message || "Verification failed. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    if (!storedPhone) {
      setError("Verification details not found. Please go back and try again.");
      return;
    }

    setIsResending(true);
    try {
      await resendOtp({ phone: storedPhone });
      setResendCountdown(60);
    } catch (err) {
      setError(err?.message || "Failed to resend OTP. Please try again.");
    } finally {
      setIsResending(false);
    }
  };

  const container = {
    hidden: { opacity: 0, x: 30 },
    visible: { opacity: 1, x: 0, transition: { staggerChildren: 0.08 } },
    exit: { opacity: 0, x: -30 },
  };

  const item = {
    hidden: { opacity: 0, y: 8 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <motion.div
      variants={container}
      initial="hidden"
      animate="visible"
      exit="exit"
      className="container d-flex justify-content-center py-4"
    >
      <div className="w-100" style={{ maxWidth: 420 }}>
        <motion.button
          variants={item}
          type="button"
          onClick={() => goback?.()}
          className="btn btn-link p-0 d-flex align-items-center gap-2 mb-4"
        >
          <ArrowLeft size={16} />
          <span>Back</span>
        </motion.button>

        <motion.div variants={item} className="d-flex justify-content-center mb-4">
          <div
            className="d-flex align-items-center justify-content-center rounded-3"
            style={{ width: 64, height: 64, background: "rgba(13,110,253,.1)" }}
          >
            <Phone size={32} className="text-primary" />
          </div>
        </motion.div>

        <motion.div variants={item} className="text-center mb-4">
          <h2 className="fw-bold mb-2">Check your phone</h2>
          <p className="text-muted mb-1">We sent a verification code to</p>
          <p className="fw-medium">{storedPhone}</p>
        </motion.div>

        <motion.div variants={item} onPaste={handlePaste}>
          <div className="d-flex justify-content-center gap-2 mb-3">
            {code.map((digit, i) => (
              <motion.input
                key={i}
                layout
                ref={(el) => {
                  inputRefs.current[i] = el;
                }}
                value={digit}
                disabled={isLoading}
                inputMode="numeric"
                maxLength={1}
                onChange={(e) => handleChange(i, e.target.value)}
                onKeyDown={(e) => handleKeyDown(i, e)}
                className={`form-control text-center ${error ? "border-danger" : ""}`}
                style={{ width: 48, height: 56, fontSize: 20, fontWeight: 600 }}
              />
            ))}
          </div>

          <AnimatePresence>
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                className="text-danger text-center small mb-3"
              >
                {error}
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        <AnimatePresence>
          {isLoading && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="d-flex justify-content-center mb-4"
            >
              <span className="spinner-border text-primary" />
            </motion.div>
          )}
        </AnimatePresence>

        <motion.div variants={item} className="text-center">
          <p className="text-muted small mb-1">Didn’t receive the code?</p>
          <motion.button
            whileHover={{ scale: resendCountdown ? 1 : 1.03 }}
            whileTap={{ scale: 0.96 }}
            onClick={handleResend}
            disabled={resendCountdown > 0 || isResending || isLoading}
            className="btn btn-link d-inline-flex align-items-center gap-2"
          >
            {resendCountdown ? `Resend in ${resendCountdown}s` : "Resend code"}
            {isResending && <Spinner size="sm" animation="border" className="ms-1" />}
          </motion.button>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default VerificationForm;
